import { apiSdk } from '@/httpClient';
import { LoadingOutlined } from '@ant-design/icons';
import { Button, Modal, Upload } from 'antd';
import React, { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BlobStorageContainerType } from '@/constants';
import { fileService } from '@/services/fileService';

// Conteneurs privés : aperçu par l'API (droits et compagnie vérifiés), jamais par l'URL du blob.
const mediaContainers: number[] = [BlobStorageContainerType.EmployeesContainer, BlobStorageContainerType.BusContainer, BlobStorageContainerType.TransportPassengers];
// Lus seulement par leur point d'accès dédié (justificatifs de solde) : pas d'aperçu ici.
const dedicatedContainers: number[] = [BlobStorageContainerType.StationBalanceProofs, BlobStorageContainerType.ImportLogs];
const fileNameOf = (url: string) => decodeURIComponent((url || '').split('/').pop() || url);

interface Props {
  // blobStorageContainer
  blobStorageContainer: number;
  multiple?: boolean;
  maxFileSize?: number;
  onChange?(value: string | string[] | null | undefined): void;
  value?: string | string[] | null | undefined;
  disabled?: boolean;
}

export default function SingleImageUpload({ multiple, maxFileSize = 10, onChange, value, blobStorageContainer, disabled }: Props): ReactElement {
  const { t } = useTranslation();
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [fileList, updateFileList] = useState<any[]>([]);

  function beforeUpload(fileSize: number): boolean {
    if (fileSize / 1024 / 1024 > maxFileSize) {
      Modal.error({
        title: t('singleUpload_Error'),
        content: t('singleUpload_ErrorMaxFileSize', { maxFileSize: maxFileSize }),
      });
      return false;
    }
    return true;
  }
  function handleRemove(fileUrl: string) {
    const urls = fileList.map(x => x.url).filter(x => x !== fileUrl);

    if (onChange) {
      if (multiple) {
        onChange(urls);
      } else {
        onChange(null);
      }
    }
  }

  React.useEffect(() => {
    if (value) {
      if (Array.isArray(value)) {
        updateFileList(
          value.map((x, i) => {
            return { uid: (i + 1).toString(), status: 'done', url: x, name: fileNameOf(x) };
          }),
        );
      } else {
        updateFileList([
          {
            uid: '1',
            status: 'done',
            url: value,
            name: fileNameOf(value),
          },
        ]);
      }
    } else {
      updateFileList([]);
    }
  }, [value]);
  const manualUpload = async options => {
    setLoadingUpload(true);
    apiSdk.MediaService.uploadImage({
      container: blobStorageContainer,
      file: options.file,
    })
      .then(res => {
        if (res.success) {
          if (multiple) {
            if (onChange) {
              const urls = fileList.map(x => x.url);
              urls.push(res.url);
              onChange(urls);
            }
          } else {
            if (onChange) {
              onChange(res.url || '');
            }
          }
        }
      })
      .finally(() => {
        setLoadingUpload(false);
      });
  };
  const uploadButton = React.useMemo(() => {
    return (
      <Button type="default">
        <div className="ant-upload-text">{t('singleUpload_Upload')}</div>
      </Button>
    );
  }, []);
  return (
    <Upload
      disabled={disabled}
      listType="text"
      multiple={!!multiple}
      accept={'.png,.jpg,.jpeg,.gif'}
      fileList={fileList}
      beforeUpload={file => beforeUpload(file.size)}
      customRequest={manualUpload}
      onRemove={file => handleRemove(file.url || '')}
      onPreview={file => {
        if (mediaContainers.includes(blobStorageContainer)) {
          fileService.openMedia(file.url || '', blobStorageContainer);
        } else if (!dedicatedContainers.includes(blobStorageContainer)) {
          window.open(file.url, '_blank');
        }
      }}
    >
      {loadingUpload ? <LoadingOutlined /> : multiple ? uploadButton : fileList?.length ? null : uploadButton}
    </Upload>
  );
}
